const ANALYTICS_LAMBDA_URL = process.env.REACT_APP_ANALYTICS_LAMBDA_URL || "";
const ANALYTICS_API_KEY = process.env.REACT_APP_ANALYTICS_API_KEY || "";

/**
 * Get or create a persistent anonymous visitor ID.
 * Stored in localStorage so it survives page reloads but is unique per browser.
 */
function getVisitorId() {
    const KEY = "site_visitor_id";
    let id = localStorage.getItem(KEY);
    if (!id) {
        id = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(KEY, id);
    }
    return id;
}

/**
 * Track a page view. Fire-and-forget — errors are silently ignored
 * so tracking never degrades the user experience.
 *
 * @param {string} page – the pathname, e.g. "/" or "/books"
 */
export function trackPageView(page) {
    if (!ANALYTICS_LAMBDA_URL) return;

    const body = {
        action: "track",
        page,
        referrer: document.referrer || "direct",
        visitorId: getVisitorId(),
    };

    // Use navigator.sendBeacon for reliability (survives page unloads),
    // fall back to fetch.
    const payload = JSON.stringify(body);

    try {
        // sendBeacon doesn't support custom headers, so we use fetch instead
        // to include the API key.
        fetch(ANALYTICS_LAMBDA_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": ANALYTICS_API_KEY,
            },
            body: payload,
            keepalive: true, // survives page unloads like sendBeacon
        }).catch(() => {}); // silently ignore
    } catch {
        // silently ignore
    }
}

/**
 * Fetch analytics data from the Analytics Lambda (admin only).
 *
 * @param {string} password  – admin password
 * @param {string} query     – "summary" | "daily" | "pages" | "referrers"
 * @param {number} [days=30] – look-back window (1–90)
 * @returns {Promise<{ query: string, days: number, data: object[] }>}
 */
export async function fetchAnalytics(password, query, days = 30) {
    const response = await fetch(ANALYTICS_LAMBDA_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Api-Key": ANALYTICS_API_KEY,
        },
        body: JSON.stringify({ action: "query", password, query, days }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to fetch analytics");
    }

    return response.json();
}

